import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styles from './Navbar.module.css';
import logo from '../../assets/logo1.svg';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Menu', to: '/menu' },
  { label: 'Meet The Brews', to: '/brews' },
  { label: 'Life at Berli', to: '/gallery' },
  { label: 'Contact Us', to: '/contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const prevScrollY = useRef(0);
  const ticking = useRef(false);
  const drawerOpenRef = useRef(false);

  const location = useLocation();

  // keep a ref in sync so the scroll listener can read it without re-subscribing
  useEffect(() => { drawerOpenRef.current = drawerOpen; }, [drawerOpen]);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location]);

  // Lock scroll while drawer is open and prevent layout shift
  useEffect(() => {
    if (drawerOpen) {
      // Calculate exact scrollbar width to prevent layout jump
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      // Set CSS variable so the fixed navbar can pad itself too
      document.documentElement.style.setProperty('--scroll-pad', `${scrollbarWidth}px`);
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.setProperty('--scroll-pad', '0px');
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.setProperty('--scroll-pad', '0px');
    };
  }, [drawerOpen]);

  // Scroll handler — subscribe ONCE, throttle with rAF, read state from refs
  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 20);
        setHidden(y > prevScrollY.current && y > 120 && !drawerOpenRef.current);
        prevScrollY.current = y;
        ticking.current = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <nav
        className={[
          styles.navbar,
          scrolled ? styles.scrolled : '',
          hidden ? styles.hidden : '',
          drawerOpen ? styles.menuActive : '',
        ].filter(Boolean).join(' ')}
        aria-label="Main navigation"
      >
        {/* ── Left: Hamburger + Book a Table CTA ── */}
        <div className={styles.leftGroup}>
          <button
            className={[styles.hamburger, drawerOpen ? styles.hamburgerOpen : ''].filter(Boolean).join(' ')}
            onClick={() => setDrawerOpen(o => !o)}
            aria-expanded={drawerOpen}
            aria-controls="nav-drawer"
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
          >
            <span />
            <span />
            <span />
          </button>

          <NavLink to="/reservation" className={styles.ctaBtn} aria-label="Book a table">
            Book a Table
          </NavLink>
        </div>

        {/* ── Center: Logo ── */}
        <NavLink to="/" className={styles.logoContainer} aria-label="Berli Brewing Co. — Home">
          <img src={logo} alt="Berli Brewing Co." className={styles.logoImg} />
        </NavLink>

        {/* ── Right: Meet The Brews CTA ── */}
        <NavLink to="/brews" className={styles.brewsBtn} aria-label="Meet the brews">
          Meet The Brews
        </NavLink>
      </nav>

      {/* ── Drawer Sidebar ── */}
      <div
        id="nav-drawer"
        className={[styles.drawer, drawerOpen ? styles.drawerOpen : ''].filter(Boolean).join(' ')}
        aria-hidden={!drawerOpen}
      >
        <div className={styles.backdrop} onClick={() => setDrawerOpen(false)} />

        <div className={styles.drawerPanel}>
          <ul className={styles.drawerLinks} role="list">
            {NAV_LINKS.map(({ label, to }, i) => (
              <li key={to} className={styles.drawerLinkItem} style={{ '--i': i }}>
                <NavLink
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) =>
                    [styles.drawerLink, isActive ? styles.drawerLinkActive : ''].filter(Boolean).join(' ')
                  }
                  onClick={() => setDrawerOpen(false)}
                >
                  <span className={styles.drawerLinkNumber}>0{i + 1}</span>
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>

          <NavLink to="/reservation" className={styles.drawerCtaBtn} onClick={() => setDrawerOpen(false)}>
            Book a Table
          </NavLink>
        </div>
      </div>
    </>
  );
};

export default Navbar;